/**
 * The three per-variant content block editors' write paths — each a thin wrapper over a store
 * action, because a component must never `v-model` onto a document field (B9).
 *
 * The owner and the index arrive as GETTERS: a block's index changes under every reorder and
 * removal above it, so a snapshot would write into the wrong block.
 *
 * Image and audio blocks carry a plain URL string (B4 skipped the media path), so there is no
 * uploader here. `AssetRef` is `{ url }`, so one drops in later without migrating a definition.
 */
import type { ContentBlockOwner } from './store/interfaces'

import { useAssessmentEditorStore } from './store'

interface UseContentBlockEditorOptions {
  owner: () => ContentBlockOwner
  index: () => number
}

interface UseContentBlockTextReturn {
  setValue: (value: string) => void
}

interface UseContentBlockImageReturn {
  setUrl: (value: string) => void
  setAlt: (value: string) => void
}

interface UseContentBlockAudioReturn {
  setUrl: (value: string) => void
}

export const useContentBlockText = (
  { owner, index }: UseContentBlockEditorOptions,
): UseContentBlockTextReturn => {
  const store = useAssessmentEditorStore()

  const setValue = (value: string): void => {
    store.updateTextBlock(owner(), index(), { value })
  }

  // No `setAudioUrl`. Audio on a text block is not authorable — that is the audio block's job —
  // but `TextBlock.audioUrl` is still read by the media manifest, so a stored block that carries
  // one keeps working and nothing here strips it (B9).

  return { setValue }
}

export const useContentBlockImage = (
  { owner, index }: UseContentBlockEditorOptions,
): UseContentBlockImageReturn => {
  const store = useAssessmentEditorStore()

  const setUrl = (value: string): void => {
    store.updateImageBlock(owner(), index(), { url: value })
  }

  const setAlt = (value: string): void => {
    store.updateImageBlock(owner(), index(), { alt: value })
  }

  return { setUrl, setAlt }
}

export const useContentBlockAudio = (
  { owner, index }: UseContentBlockEditorOptions,
): UseContentBlockAudioReturn => {
  const store = useAssessmentEditorStore()

  const setUrl = (value: string): void => {
    store.updateAudioBlock(owner(), index(), { url: value })
  }

  return { setUrl }
}
